import Head from 'next/head';
import React, { useRef } from 'react';

import seoScriptProps from 'utilities/src/seoScriptProps';

import Breadcrumbs, { type BreadcrumbsItemType } from 'ui/src/components/Breadcrumbs';
import FloatingWidgets from 'ui/src/components/Widgets/FloatingWidgets';

import { BREADCRUMBS_BASE } from 'utils/shared';

import type { SinglePostProps } from 'pages/[slug]';

import TrustBar from 'layout/src/components/TrustBar';
import { widgets } from 'data/widgets';

import PostBody from './PostBody';
import LeavePreviewButton from './LeavePreviewButton';

import BlogWidgets from './BlogWidgets';
import HeroPost from './HeroPost';
import ProgressBar from './ProgressBar';

export type SlugPageProps = Omit<SinglePostProps, 'seo' | 'bgFooter'> & {
  url: string;
};

export default function SlugPage({
  post,
  hideAuthor,
  hideSharing,
  keyQuestions,
  partOfSeries,
  postSeries,
  url,
  preview,
  errors,
  keyQuestionLdJson,
  previousArticle,
  nextArticle,
  relatedArticles,
  relatedServices,
  hasArticlesContentsLinks,
}: SlugPageProps) {
  const breadcrumbsItems: BreadcrumbsItemType[] = [
    ...BREADCRUMBS_BASE,
    {
      label: post.categories?.nodes?.[0]?.name ?? '',
      href: `/category/${post.categories?.nodes?.[0]?.slug}/`,
    },
    {
      label: post.title ?? '',
    },
  ];
  const scriptProps = seoScriptProps(keyQuestionLdJson);
  const postRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Head>
        <title>{post.title}</title>
        {scriptProps && <script {...scriptProps} />}
      </Head>
      {preview && <LeavePreviewButton />}
      <ProgressBar postRef={postRef} />

      <TrustBar />
      <Breadcrumbs
        items={breadcrumbsItems}
        iconName="Home"
        iconClasses="bg-site-neutrals-700 -mr-3"
        customListClasses="mx-6 mb-8 hidden md:flex lg:mx-[9.25rem] 2xl:m-auto 2xl:w-[89.5rem]"
      />
      <div className="m-6 md:mb-16">
        <HeroPost errors={errors} post={post} preview={preview} className="mdL:hidden hidden md:block" />
      </div>
      <div className="lgS:mx-8 mdL:gap-[3.25rem] lgS:gap-[8.5rem] mdL:relative mdL:mb-32 mx-4 mb-10 flex flex-col justify-between md:mb-24 md:flex-row md:gap-6 lg:mx-[9.25rem] 2xl:mx-auto 2xl:w-[89.5rem] 2xl:gap-40">
        <div ref={scrollRef}>
          <div ref={postRef}>
            <HeroPost
              errors={errors}
              post={post}
              preview={preview}
              className="mdL:block mdL:mb-16 lgS:w-[49.75rem] md:hidden"
            />
            <div className="relative">
              <PostBody
                post={post}
                keyQuestions={keyQuestions}
                partOfSeries={partOfSeries}
                postSeries={postSeries}
                hideAuthor={hideAuthor}
                hideSharing={hideSharing}
                url={url}
                previousArticle={previousArticle}
                nextArticle={nextArticle}
                hasArticlesContentsLinks={hasArticlesContentsLinks}
              />
            </div>
          </div>

          <Breadcrumbs
            items={breadcrumbsItems}
            iconName="Home"
            iconClasses="bg-site-neutrals-700 -translate-x-1 -mr-3"
            customListClasses="mb-8 mt-12 box-content max-w-7xl mdSL:mb-12 mdSL:px-4 flex md:hidden"
          />
        </div>

        <BlogWidgets relatedArticles={relatedArticles} relatedServices={relatedServices} />
        <FloatingWidgets
          widgets={widgets}
          scrollRef={scrollRef}
          relatedArticles={relatedArticles}
          relatedServices={relatedServices}
        />
      </div>
    </>
  );
}
